import Autocomplete from "../../library/Autocomplete";
import {IncludeExclude, PublisherOrSiteOption, PublisherRecord, PublisherSiteRecord, AUTOCOMPLETE_GROUP_HEADER_PADDING, AUTOCOMPLETE_GROUP_ITEM_PADDING} from "../../../utils/types";
import { checkForMatch } from "../../../utils/misc";
import { Form, Button } from "react-bootstrap";
import './TrafficSourcesAutocomplete.css';
import { useState } from "react";
import { isAllowedFilterField } from "../../../utils/UserHelper";
import { Menu, MenuItem, MenuProps } from "react-bootstrap-typeahead";

interface GroupedTrafficSourceOption {
  id: string;
  publisherId: string;
  siteId: string | null;
  publisherName: string;
  siteName: string | null;
  key: string;
  isGroup?: boolean;
  disabled?: boolean;
}

/**
 * The text used when showing the selected options as labels that can be [X]ed out individually
 * does not influence the rendering of the menu, just of the selected items
 *
 * Technically, option could just be a PublisherRecord, not a PublisherSiteRecord, but they have
 * mostly the same signature, except for the bits we check manually.
 */
export const TrafficSourceLabelKeyFunction = function (option: PublisherOrSiteOption) {
  const publisher:PublisherRecord = (option.record as PublisherRecord);
  const pubSite:PublisherSiteRecord = (option.record as PublisherSiteRecord);

  if ( pubSite && pubSite.apptapSiteId ){
    return pubSite.apptapPublisherName + " - " + pubSite.apptapPublisherSiteName + " (" + pubSite.apptapPublisherId + ":" + pubSite.apptapSiteId + ")";
  }
  else {
    return publisher.apptapPublisherName + " (" + publisher.apptapPublisherId + ":<all sites>)";
  }
}

interface TrafficSourcesAutocompleteProps {
  onChange?: (newSelected:PublisherOrSiteOption[], isSelectedFromCheckbox?:boolean) => void;
  onInputChange?: (query: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onMenuToggle?: (isOpen:boolean) => void;
  reactRef: any;
  options: PublisherOrSiteOption[];
  selected?: PublisherOrSiteOption[];
  disabled?: boolean;
  multiple?: boolean;
  selectedForCheckboxes?: PublisherOrSiteOption[];
  usePublisherDisabled?: boolean;
  useCheckbox?: boolean;
  style?: {[key:string]:string};
  useIncludeExclude?: boolean;
  defaultIncludeExclude?: IncludeExclude;
  includeExcludeOnChange?: (newIncludeExclude:IncludeExclude) => void;
}

function TrafficSourcesAutocomplete(props: TrafficSourcesAutocompleteProps) {
  const [arrowMode, setArrowMode] = useState(false);

  // Group traffic sources by publisher name
  const groupedOptions = (): GroupedTrafficSourceOption[] => {
    if (!props.options || props.options.length === 0) {
      return [];
    }

    const groups: {[key: string]: PublisherOrSiteOption[]} = {};

    // Group by publisher
    props.options.forEach(option => {
      const publisher: PublisherRecord = option.record as PublisherRecord;
      const pubSite: PublisherSiteRecord = option.record as PublisherSiteRecord;

      const publisherId = pubSite.apptapPublisherId || publisher.apptapPublisherId;
      const publisherName = pubSite.apptapPublisherName || publisher.apptapPublisherName;

      const groupKey = `${publisherId}_${publisherName}`;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(option);
    });

    const result: GroupedTrafficSourceOption[] = [];

    // Create grouped structure - sort by publisher name instead of groupKey
    Object.keys(groups).sort((a, b) => {
      const groupA = groups[a];
      const groupB = groups[b];
      const publisherNameA = (groupA[0].record as PublisherSiteRecord).apptapPublisherName || (groupA[0].record as PublisherRecord).apptapPublisherName;
      const publisherNameB = (groupB[0].record as PublisherSiteRecord).apptapPublisherName || (groupB[0].record as PublisherRecord).apptapPublisherName;
      return publisherNameA.localeCompare(publisherNameB);
    }).forEach(groupKey => {
      const groupItems = groups[groupKey];
      const firstItem = groupItems[0];
      const publisher: PublisherRecord = firstItem.record as PublisherRecord;
      const pubSite: PublisherSiteRecord = firstItem.record as PublisherSiteRecord;

      const publisherId = pubSite.apptapPublisherId || publisher.apptapPublisherId;
      const publisherName = pubSite.apptapPublisherName || publisher.apptapPublisherName;

      // Add group header (publisher)
      result.push({
        id: `group_${publisherId}`,
        publisherId: publisherId,
        siteId: null,
        publisherName: publisherName,
        siteName: null,
        key: publisherId,
        isGroup: true,
        disabled: false
      });

      // Add group members (sites) sorted by site name
      groupItems
        .filter(item => {
          const site: PublisherSiteRecord = item.record as PublisherSiteRecord;
          return site.apptapSiteId; // Only include actual sites, not publisher-level items
        })
        .sort((a, b) => {
          const siteA: PublisherSiteRecord = a.record as PublisherSiteRecord;
          const siteB: PublisherSiteRecord = b.record as PublisherSiteRecord;
          return (siteA.apptapPublisherSiteName || "").localeCompare(siteB.apptapPublisherSiteName || "");
        })
        .forEach(item => {
          const site: PublisherSiteRecord = item.record as PublisherSiteRecord;
          result.push({
            id: item.id,
            publisherId: site.apptapPublisherId,
            siteId: site.apptapSiteId,
            publisherName: site.apptapPublisherName,
            siteName: site.apptapPublisherSiteName,
            key: item.key,
            isGroup: false,
            disabled: item.disabled || false
          });
        });
    });

    return result;
  };

  const labelKeyFunction = (option: GroupedTrafficSourceOption | PublisherOrSiteOption) => {
    if ('isGroup' in option && option.isGroup) {
      return option.publisherName + " (" + option.publisherId + ")";
    }
    if ('key' in option && option.key) {
      // For selected items (PublisherOrSiteOption)
      return TrafficSourceLabelKeyFunction(option as PublisherOrSiteOption);
    }
    // For grouped items
    const grouped = option as GroupedTrafficSourceOption;
    return grouped.siteName + " (" + grouped.publisherId + ":" + grouped.siteId + ")";
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch(e.code) {
      case "Enter":
        const activeIndex = props.reactRef?.current?.state?.activeIndex;
        props.reactRef?.current?.toggleMenu();
        if (props.reactRef?.current?.setState) {
          props.reactRef.current.setState({...props.reactRef.current.state, activeIndex: activeIndex});
        }
        break;
      case "ArrowDown":
      case "ArrowUp":
        setArrowMode(true);
        break;
      case "Space":
        if (arrowMode && props.reactRef?.current?.state?.activeItem) {
          e.preventDefault();
          handleClick(e, props.reactRef.current.state.activeItem);
        }
        break;
      default:
        setArrowMode(false);
        break;
    }
  };

  const handleClick = (e: any, selectedValue: GroupedTrafficSourceOption, currentlyVisibleResults?: GroupedTrafficSourceOption[]) => {
    e.stopPropagation();

    if (selectedValue.isGroup) {
      // For group selection, only toggle sites that are CURRENTLY VISIBLE in the filtered results
      const publisherId = selectedValue.publisherId;

      // Get only the visible items for this group (respects current filter)
      const visibleGroupItems = currentlyVisibleResults
        ? currentlyVisibleResults.filter(opt => !opt.isGroup && opt.publisherId === publisherId)
        : groupedOptions().filter(opt => !opt.isGroup && opt.publisherId === publisherId);

      // Check if ALL visible group items are currently selected
      const selectedGroupItems = visibleGroupItems.filter(item =>
        props.selectedForCheckboxes?.some(selected => selected.key === item.key)
      );
      const isGroupFullySelected = selectedGroupItems.length === visibleGroupItems.length && visibleGroupItems.length > 0;

      if (isGroupFullySelected) {
        // If all visible items are selected, unselect all visible items (toggle off)
        const visibleKeys = new Set(visibleGroupItems.map(item => item.key));
        const remainingSelection = (props.selectedForCheckboxes || [])
          .filter(selected => !visibleKeys.has(selected.key));

        props.onChange(remainingSelection, false);
      } else {
        // Otherwise, select all visible items that aren't already selected
        const existingSelection = props.selectedForCheckboxes || [];
        const existingKeys = new Set(existingSelection.map(s => s.key));

        const itemsToAdd = visibleGroupItems
          .filter(item => !existingKeys.has(item.key))
          .map(item => props.options.find(opt => opt.key === item.key))
          .filter((item): item is PublisherOrSiteOption => item !== undefined);

        const fullSelection = [...existingSelection, ...itemsToAdd];
        props.onChange(fullSelection, false);
      }
    } else {
      // Individual site selection - convert GroupedTrafficSourceOption to PublisherOrSiteOption
      const found = props.options.find(opt => opt.key === selectedValue.key);
      if (found) {
        const existingSelection = props.selectedForCheckboxes || [];
        const existingIndex = existingSelection.findIndex(s => s.key === found.key);

        if (existingIndex !== -1) {
          // Remove if already selected
          const newSelection = [...existingSelection];
          newSelection.splice(existingIndex, 1);
          props.onChange(newSelection, false);
        } else {
          // Add if not selected
          props.onChange([...existingSelection, found], false);
        }
      }
    }
  };

  const handleRegularOnChange = (selected: PublisherOrSiteOption[]) => {
    // Detect if handleClick already handled this (duplicate in selection)
    const selectedKeys = selected.map((s: PublisherOrSiteOption) => s.key);
    const hasDuplicate = selectedKeys.length !== new Set(selectedKeys).size;

    if (hasDuplicate || (selected.length > 0 && selected[selected.length - 1] &&
        props.selected?.some((s: PublisherOrSiteOption) => s.key === selected[selected.length - 1].key))) {
      return; // handleClick already handled it
    }

    // Pass through X button removals and clear button
    props.onChange(selected, false);
  };

  const renderMenuItemChildren = (option: GroupedTrafficSourceOption, menuProps: any, index: number, currentlyVisibleResults?: GroupedTrafficSourceOption[]) => {
    const isGroup = option.isGroup;
    const isChecked = props.selectedForCheckboxes?.some(selected => selected.key === option.key) || false;

    // For group headers, check if all VISIBLE items in group are selected
    let groupCheckState: boolean | "indeterminate" = false;
    if (isGroup) {
      // Get only the visible items for this group (respects current filter)
      const visibleGroupItems = currentlyVisibleResults
        ? currentlyVisibleResults.filter(opt => !opt.isGroup && opt.publisherId === option.publisherId)
        : groupedOptions().filter(opt => !opt.isGroup && opt.publisherId === option.publisherId);

      const selectedGroupItems = visibleGroupItems.filter(item =>
        props.selectedForCheckboxes?.some(selected => selected.key === item.key)
      );
      if (selectedGroupItems.length === visibleGroupItems.length && visibleGroupItems.length > 0) {
        groupCheckState = true;
      } else if (selectedGroupItems.length > 0) {
        groupCheckState = "indeterminate";
      }
    }

    const checkboxElement = props.useCheckbox && !option.disabled ? (
      <Form.Check
        key={option.id + '-' + (isGroup ? groupCheckState.toString() : isChecked.toString())}
        inline
        readOnly
        style={{ whiteSpace: "nowrap", margin: "0 5px", cursor: "pointer" }}
        type="checkbox"
        disabled={option.disabled}
        checked={isGroup ? groupCheckState === true : isChecked}
        ref={(input: any) => {
          if (input && isGroup && groupCheckState === "indeterminate") {
            input.indeterminate = true;
          }
        }}
        onClick={(e: any) => {
          e.stopPropagation();
          handleClick(e, option, currentlyVisibleResults);
        }}
      />
    ) : null;

    const displayId = isGroup ? option.publisherId : option.siteId;
    const displayName = isGroup
      ? option.publisherName
      : option.siteName;

    return (
      <div
        style={{
          cursor: "pointer",
          padding: isGroup ? AUTOCOMPLETE_GROUP_HEADER_PADDING : AUTOCOMPLETE_GROUP_ITEM_PADDING,
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: isGroup ? "#f1f3f4" : "transparent",
          borderBottom: isGroup ? "1px solid #e9ecef" : "none"
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleClick(e, option, currentlyVisibleResults);
        }}
      >
        <strong style={{ display: "flex", marginLeft: !checkboxElement && !option.disabled ? "20px" : undefined }}>
          {checkboxElement}{displayName}
        </strong>
        <span style={{ float: "right", color: "#717171", fontFamily: isGroup ? "inherit" : "monospace" }}>
          {displayId}
        </span>
      </div>
    );
  };

  const filterPublisherSites = (option: GroupedTrafficSourceOption, filterProps: any) => {
    if (!filterProps.text || filterProps.text.length === 0) return true;

    const searchString = (filterProps.text as string).trim().toLowerCase();
    const searchStrings: string[] = searchString.split(" ");

    // For group headers, only show if at least one child matches the filter
    if (option.isGroup) {
      const childItems = groupedOptions().filter(opt =>
        !opt.isGroup && opt.publisherId === option.publisherId
      );

      // Check if any child item matches the search
      return childItems.some(child => {
        const childSearchText = [
          child.publisherName,
          child.siteName,
          child.publisherId,
          child.siteId
        ].filter(Boolean).join(" ").toLowerCase();

        return checkForMatch(searchStrings, childSearchText);
      });
    }

    // For individual items, search in publisher name, site name, publisher ID, site ID
    const searchText = [
      option.publisherName,
      option.siteName,
      option.publisherId,
      option.siteId
    ].filter(Boolean).join(" ").toLowerCase();

    return checkForMatch(searchStrings, searchText);
  };

  const renderMenu = (results: GroupedTrafficSourceOption[], menuProps: MenuProps) => {
    // Get only the VISIBLE non-group items from filtered results
    const visibleOptions = results.filter(opt => !opt.isGroup);
    const visibleSelectedCount = visibleOptions.filter(opt =>
      props.selectedForCheckboxes?.some(selected => selected.key === opt.key)
    ).length;
    const allVisibleSelected = visibleSelectedCount === visibleOptions.length && visibleOptions.length > 0;
    const someVisibleSelected = visibleSelectedCount > 0;

    return (
      <Menu {...menuProps}>
        <div style={{
          display: "flex",
          gap: "0.5rem",
          padding: "0 0.5rem 0.5rem 0.5rem",
          borderBottom: "1px solid #e9ecef",
          backgroundColor: "#f8f9fa"
        }}>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={(e) => {
              e.stopPropagation();
              if (!allVisibleSelected) {
                // Select all VISIBLE options - merge with existing selections
                const existingSelection = props.selectedForCheckboxes || [];
                const existingKeys = new Set(existingSelection.map(s => s.key));

                // Get the actual PublisherOrSiteOption objects for visible items
                const newSelections = visibleOptions
                  .filter(opt => !existingKeys.has(opt.key))
                  .map(opt => props.options.find(o => o.key === opt.key))
                  .filter((opt): opt is PublisherOrSiteOption => opt !== undefined);

                // Merge: existing + new visible items
                const fullSelection = [...existingSelection, ...newSelections];
                props.onChange(fullSelection, false);
              }
            }}
            style={{ flex: 1, fontSize: "0.875rem" }}
          >
            Select All
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={(e) => {
              e.stopPropagation();
              if (someVisibleSelected) {
                // Deselect all VISIBLE selected items - remove from existing selections
                const visibleKeys = new Set(visibleOptions.map(opt => opt.key));
                const remainingSelection = (props.selectedForCheckboxes || [])
                  .filter(selected => !visibleKeys.has(selected.key));

                props.onChange(remainingSelection, false);
              }
            }}
            style={{ flex: 1, fontSize: "0.875rem" }}
          >
            Deselect All
          </Button>
        </div>
        {results.map((result, index) => (
          <MenuItem key={index} option={result} position={index}>
            {renderMenuItemChildren(result, menuProps, index, results)}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  return (
    <Autocomplete
      id="trafficSourcesAutocomplete"
      className="traffic-sources-grouped autocomplete-keep-open"
      renderMenu={props.useCheckbox ? renderMenu : undefined}
      renderMenuItemChildren={renderMenuItemChildren}
      placeholder="Publishers and Sites"
      reactKey="trafficSourcesAutocomplete"
      labelKey={labelKeyFunction}
      onChange={handleRegularOnChange}
      onKeyDown={handleKeyDown}
      options={groupedOptions()}
      selected={props.selected}
      filterBy={filterPublisherSites}
      reactRef={props.reactRef}
      disabled={props.disabled}
      multiple={props.multiple}
      style={props.style}
      onInputChange={props.onInputChange}
      onMenuToggle={props.onMenuToggle}
      useIncludeExclude={props.useIncludeExclude && isAllowedFilterField("trafficSourcesExclude")}
      defaultIncludeExclude={props.defaultIncludeExclude}
      includeExcludeOnChange={props.includeExcludeOnChange}
      maxDisplayTokens={5}
    />
  );
}

export default TrafficSourcesAutocomplete;